/**
 * Kontent CDN image transform presets.
 *
 * Pick a preset by layout context → breakpoint:
 *
 *   imagePreset.fullWidth.desktop   → 1920px, q90  (mastheads, banners)
 *   imagePreset.fullWidth.tablet    → 1024px, q90
 *   imagePreset.fullWidth.mobile    →  720px, q90
 *   imagePreset.halfWidth.desktop   → 1200px, q85  (image + text side-by-side)
 *   imagePreset.halfWidth.mobile    →  720px, q85
 *   imagePreset.card.desktop        →  800px, q85  (product cards, showcases)
 *   imagePreset.card.tablet         →  600px, q85
 *   imagePreset.card.mobile         →  400px, q85
 *   imagePreset.inline.large        →  800px, q85  (rich-text images)
 *   imagePreset.inline.small        →  400px, q85  (floated in-copy images)
 *   imagePreset.decorative.desktop  → 1200px, q75  (ghosted / background)
 *   imagePreset.decorative.mobile   →  720px, q75
 *
 * WebP sources: the normalize step omits `fm` and `q` so the CDN resizes
 * only and does not re-encode already-optimized WebP uploads.
 */

export interface KontentImageParams {
    w?: number;
    h?: number;
    fit?: 'clip' | 'crop' | 'scale';
    fm?: 'webp' | 'jpg' | 'png' | 'gif';
    q?: number;
    auto?: 'format';
}

export const imagePreset = {
    /** Mastheads, hero banners — spans full viewport width */
    fullWidth: {
        /** 1920px · q90 */
        desktop: { w: 1920, auto: 'format' as const, q: 90 },
        /** 1024px · q90 */
        tablet: { w: 1024, auto: 'format' as const, q: 90 },
        /** 720px · q90 */
        mobile: { w: 720, auto: 'format' as const, q: 90 },
    },
    /** Image + text side-by-side, ~50% viewport width */
    halfWidth: {
        /** 1200px · q85 */
        desktop: { w: 1200, auto: 'format' as const, q: 85 },
        /** 720px · q85 */
        mobile: { w: 720, auto: 'format' as const, q: 85 },
    },
    /** Product cards, showcases — bounded-width containers */
    card: {
        /** 800px · q85 — matches typical card display width */
        desktop: { w: 800, auto: 'format' as const, q: 85 },
        /** 600px · q85 — tablet card display */
        tablet: { w: 600, auto: 'format' as const, q: 85 },
        /** 400px · q85 — mobile card display */
        mobile: { w: 400, auto: 'format' as const, q: 85 },
    },
    /** Rich-text / inline images */
    inline: {
        /** 800px · q85 — standard in-copy image */
        large: { w: 800, auto: 'format' as const, q: 85 },
        /** 400px · q85 — floated / small in-copy image */
        small: { w: 400, auto: 'format' as const, q: 85 },
    },
    /** Ghosted / background decorative images */
    decorative: {
        /** 1200px · q75 */
        desktop: { w: 1200, auto: 'format' as const, q: 75 },
        /** 720px · q75 */
        mobile: { w: 720, auto: 'format' as const, q: 75 },
    },
};

export function isKontentWebpSource(url: string): boolean {
    try {
        return /\.webp$/i.test(new URL(url).pathname);
    } catch {
        return /\.webp(\?|#|$)/i.test(url);
    }
}

/**
 * When the asset is already WebP, skip format/quality transforms so Kontent
 * only resizes (no lossy re-encode of an editor-uploaded WebP).
 */
export function normalizeKontentImageParams(
    url: string,
    params: KontentImageParams
): KontentImageParams {
    const normalized = { ...params };

    // Replace hardcoded WebP with auto-format for broader browser support
    if (normalized.fm === 'webp' && !normalized.auto) {
        delete normalized.fm;
        normalized.auto = 'format';
    }

    if (!isKontentWebpSource(url)) {
        return normalized;
    }

    // If the original source is already WebP, don't re-compress it
    const { auto: _auto, q: _q, ...resizeOnly } = normalized;
    return resizeOnly;
}

/**
 * Compute display width/height for an `<img>` tag based on the CDN target
 * width and the original image's aspect ratio.  Content authors may upload
 * oversized images; this ensures the browser reserves space using the aspect
 * ratio of the image that will actually be delivered.
 */
export function getDisplayDimensions(
    originalWidth: number | undefined,
    originalHeight: number | undefined,
    targetWidth: number
): { width: number; height: number } {
    if (originalWidth && originalHeight && originalWidth > 0) {
        const aspectRatio = originalHeight / originalWidth;
        return {
            width: targetWidth,
            height: Math.round(targetWidth * aspectRatio),
        };
    }
    return { width: targetWidth, height: targetWidth };
}

/**
 * Generate a multi-width `srcSet` string for responsive images.
 * Produces 4 widths (25%, 50%, 75%, 100% of maxWidth) so the browser
 * can pick the best fit based on the `sizes` attribute.
 */
export function buildKontentSrcSet(
    url: string | undefined,
    maxWidth: number,
    params: KontentImageParams
): string {
    if (!url) return '';
    const widths = [maxWidth * 0.25, maxWidth * 0.5, maxWidth * 0.75, maxWidth]
        .map(Math.round)
        .filter((w) => w > 0);
    return widths
        .map((w) => `${optimizeKontentImageUrl(url, { ...params, w })} ${w}w`)
        .join(', ');
}

export function optimizeKontentImageUrl(
    url: string | undefined,
    params: KontentImageParams
): string {
    if (!url || !url.includes('kc-usercontent.com')) {
        return url || '';
    }

    const normalized = normalizeKontentImageParams(url, params);
    const separator = url.includes('?') ? '&' : '?';
    const queryParts = Object.entries(normalized)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => `${k}=${v}`);

    return queryParts.length
        ? `${url}${separator}${queryParts.join('&')}`
        : url;
}
